import { FormikHelpers, Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { Card, CardHeader, CardContent } from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import {
  tenantEditAcceptableConnectionDropRate,
  tenantEditAcceptableConnectionDropRateVariables
} from "src/graphql/generated/tenantEditAcceptableConnectionDropRate";
import { EDIT_TENANT_ACCEPTABLE_CONNECTION_DROP_RATE } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";

import { numberAsStringField } from "./common";

type Values = { acceptableConnectionDropRate: string };

const MIN_ACCEPTABLE_CONNECTION_DROP_RATE = 0;
const MAX_ACCEPTABLE_CONNECTION_DROP_RATE = 0.2;
const RATE_RANGE = `between ${MIN_ACCEPTABLE_CONNECTION_DROP_RATE} and ${MAX_ACCEPTABLE_CONNECTION_DROP_RATE}`;
const RATE_RANGE_ERROR = `Must be ${RATE_RANGE}`;

export default function AcceptableConnectionDropRate(): JSX.Element {
  const tenant = useTenant()!;
  const snackbar = useSnackbar();

  const helperText =
    "Overrides the acceptable connection drop rate used by the alternate " +
    "ratio calculation for every campaign in this organization. Setting " +
    "campaign specific drop rates while this is enabled will not work. " +
    `Must be ${RATE_RANGE}. Leave blank to use each campaign's own setting ` +
    `(system default: ${tenant.acceptableConnectionDropRateDefault}).`;

  const schema = yup.object().shape({
    acceptableConnectionDropRate: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .typeError(RATE_RANGE_ERROR)
      .min(MIN_ACCEPTABLE_CONNECTION_DROP_RATE, RATE_RANGE_ERROR)
      .max(MAX_ACCEPTABLE_CONNECTION_DROP_RATE, RATE_RANGE_ERROR)
  });

  const [updateAcceptableConnectionDropRate] = useMutation<
    tenantEditAcceptableConnectionDropRate,
    tenantEditAcceptableConnectionDropRateVariables
  >(EDIT_TENANT_ACCEPTABLE_CONNECTION_DROP_RATE, {
    onErrorSnackbarMessage: `Failed to update organization settings.`
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const newAcceptableConnectionDropRate =
      values.acceptableConnectionDropRate === ""
        ? null
        : parseFloat(values.acceptableConnectionDropRate);
    const { errors, data } = await updateAcceptableConnectionDropRate({
      variables: { newAcceptableConnectionDropRate }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Organization updated.", { variant: "info" });
      helpers.resetForm({
        values: {
          acceptableConnectionDropRate: numberAsStringField(
            data!.tenantEditAcceptableConnectionDropRate
              .acceptableConnectionDropRate
          )
        }
      });
    }
  }

  return (
    <Card>
      <CardHeader title="Predictive Dialer Configuration" />
      <CardContent>
        <Formik
          initialValues={{
            acceptableConnectionDropRate: numberAsStringField(
              tenant.acceptableConnectionDropRate
            )
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ isSubmitting, dirty, errors, touched }) => (
            <Form>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Acceptable Connection Drop Rate"
                name="acceptableConnectionDropRate"
                disabled={isSubmitting}
                helperText={
                  touched.acceptableConnectionDropRate &&
                  errors.acceptableConnectionDropRate
                    ? errors.acceptableConnectionDropRate
                    : helperText
                }
              />
              <SubmitButton
                type="submit"
                disabled={!dirty}
                isSubmitting={isSubmitting}
                submitText="Update"
                loadingText="Updating..."
              />
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
